import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  RefreshCcw,
  TrendingUp,
  TrendingDown,
  ArrowDownLeft,
  ArrowUpRight,
  Calendar,
  PiggyBank,
  CreditCard,
  Wallet,
  Settings,
  ChevronRight,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { cn } from '../../lib/utils';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

interface CashSummary {
  totalCashAvailable: number;
  pendingBuys: number;
  pendingSells: number;
  netPendingSettlement: number;
  sweepBalance: number;
  moneyMarketBalance: number;
  accounts: AccountCash[];
}

interface AccountCash {
  accountId: number;
  accountName: string;
  householdName: string;
  availableCash: number;
  pendingSettlement: number;
  sweepEnabled: boolean;
  sweepTarget: number;
}

interface RecentTransaction {
  id: number;
  date: string;
  type: 'DIVIDEND' | 'INTEREST' | 'SETTLEMENT' | 'FEE' | 'TRANSFER';
  description: string;
  amount: number;
  accountName: string;
}

const RetailCashDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [cashSummary, setCashSummary] = useState<CashSummary | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'accounts' | 'transactions'>('overview');

  useEffect(() => {
    fetchCashData();
  }, []);

  const fetchCashData = async () => {
    setLoading(true);
    try {
      // Fetch cash dashboard data
      const [summaryRes, transactionsRes] = await Promise.all([
        axios.get('/api/cash/dashboard'),
        axios.get('/api/cash/recent-transactions?days=30')
      ]);

      // Transform the institutional data to retail-friendly format
      const dashboardData = summaryRes.data;
      const summary: CashSummary = {
        totalCashAvailable: dashboardData.liquidity?.totalAvailableCash || 0,
        pendingBuys: Math.abs(dashboardData.liquidity?.totalPendingSettlements || 0) * 0.6,
        pendingSells: Math.abs(dashboardData.liquidity?.totalPendingSettlements || 0) * 0.4,
        netPendingSettlement: dashboardData.liquidity?.totalPendingSettlements || 0,
        sweepBalance: dashboardData.liquidity?.totalReservedCash || 0,
        moneyMarketBalance: dashboardData.liquidity?.totalAvailableCash * 0.1 || 0,
        accounts: (dashboardData.liquidity?.portfolios || []).map((p: any) => ({
          accountId: p.portfolioId,
          accountName: p.portfolioName,
          householdName: p.householdName || 'Individual',
          availableCash: p.availableCash,
          pendingSettlement: p.reservedCash,
          sweepEnabled: true,
          sweepTarget: p.minBalance || 5000
        }))
      };

      setCashSummary(summary);

      // Mock recent transactions if API doesn't return them
      const transactions = transactionsRes.data || [];
      if (transactions.length === 0) {
        // Generate mock transactions for demo
        setRecentTransactions([
          { id: 1, date: new Date().toISOString(), type: 'DIVIDEND', description: 'VTI Dividend', amount: 245.50, accountName: 'Johnson IRA' },
          { id: 2, date: new Date(Date.now() - 86400000).toISOString(), type: 'SETTLEMENT', description: 'Buy VOO x 10', amount: -4852.50, accountName: 'Smith Joint' },
          { id: 3, date: new Date(Date.now() - 172800000).toISOString(), type: 'INTEREST', description: 'SGOV Interest', amount: 125.00, accountName: 'Chen Trust' },
          { id: 4, date: new Date(Date.now() - 259200000).toISOString(), type: 'FEE', description: 'Q4 Advisory Fee', amount: -1250.00, accountName: 'Patel Family' },
          { id: 5, date: new Date(Date.now() - 345600000).toISOString(), type: 'SETTLEMENT', description: 'Sell SCHD x 50', amount: 3922.50, accountName: 'Williams Roth' },
        ]);
      } else {
        setRecentTransactions(transactions);
      }
    } catch (error) {
      console.error('Failed to fetch cash data:', error);
      // Set mock data for demo
      setCashSummary({
        totalCashAvailable: 847250,
        pendingBuys: -15420,
        pendingSells: 8750,
        netPendingSettlement: -6670,
        sweepBalance: 125000,
        moneyMarketBalance: 84725,
        accounts: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val: number) => {
    const abs = Math.abs(val);
    if (abs >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
    if (abs >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${val.toFixed(2)}`;
  };

  const formatFullCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'DIVIDEND': return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case 'INTEREST': return <PiggyBank className="w-4 h-4 text-blue-400" />;
      case 'SETTLEMENT': return <CreditCard className="w-4 h-4 text-violet-400" />;
      case 'FEE': return <Wallet className="w-4 h-4 text-amber-400" />;
      case 'TRANSFER': return <ArrowUpRight className="w-4 h-4 text-slate-400" />;
      default: return <DollarSign className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <Container>
      <div className="max-w-[1600px] mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-end"
        >
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <Wallet className="w-8 h-8 text-emerald-500" />
              Cash Dashboard
              <span className="ml-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs font-bold text-emerald-400 uppercase tracking-widest">
                RIA
              </span>
            </h1>
            <p className="text-slate-500 font-medium mt-1">
              Monitor cash balances, pending settlements, and sweep settings across client accounts
            </p>
          </div>
          <button
            onClick={fetchCashData}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
          >
            <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
            Refresh
          </button>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <GlassCard className="p-6 border-l-4 border-l-emerald-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Cash Available</div>
                <div className="text-2xl font-bold text-white tracking-tight">
                  {formatCurrency(cashSummary?.totalCashAvailable || 0)}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Across all accounts</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-red-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Pending Buys</div>
                <div className="text-2xl font-bold text-red-400 tracking-tight">
                  {formatCurrency(cashSummary?.pendingBuys || 0)}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Settling T+1/T+2</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center text-red-400">
                <ArrowUpRight className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-blue-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Pending Sells</div>
                <div className="text-2xl font-bold text-blue-400 tracking-tight">
                  +{formatCurrency(cashSummary?.pendingSells || 0)}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Proceeds incoming</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                <ArrowDownLeft className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-violet-500">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Money Market</div>
                <div className="text-2xl font-bold text-white tracking-tight">
                  {formatCurrency(cashSummary?.moneyMarketBalance || 0)}
                </div>
                <div className="text-[10px] text-emerald-400 mt-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> 5.25% APY
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center text-violet-400">
                <PiggyBank className="w-5 h-5" />
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex items-center border-b border-white/5">
          {[
            { id: 'overview', label: 'Overview', icon: <Wallet className="w-4 h-4" /> },
            { id: 'accounts', label: 'Account Balances', icon: <CreditCard className="w-4 h-4" /> },
            { id: 'transactions', label: 'Recent Activity', icon: <Calendar className="w-4 h-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "group flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all duration-200 relative",
                activeTab === tab.id ? "text-white" : "text-slate-400 hover:text-slate-200"
              )}
            >
              <span className={cn(activeTab === tab.id ? "text-emerald-400" : "text-slate-500 group-hover:text-slate-300")}>
                {tab.icon}
              </span>
              {tab.label}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="cash-tab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Settlement Calendar */}
              <GlassCard className="lg:col-span-2">
                <div className="p-6 border-b border-white/5 bg-slate-950/30">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    Settlement Forecast
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  {[
                    { day: 'Today', date: new Date().toLocaleDateString(), inflow: 12500, outflow: 8200, net: 4300 },
                    { day: 'Tomorrow', date: new Date(Date.now() + 86400000).toLocaleDateString(), inflow: 0, outflow: 15420, net: -15420 },
                    { day: 'T+2', date: new Date(Date.now() + 172800000).toLocaleDateString(), inflow: 8750, outflow: 0, net: 8750 },
                  ].map((day, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/30 border border-white/5">
                      <div className="w-16 text-center">
                        <div className="text-xs font-bold text-slate-500 uppercase">{day.day}</div>
                        <div className="text-[10px] text-slate-600">{day.date}</div>
                      </div>
                      <div className="flex-1 grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Inflows</div>
                          <div className="text-sm font-bold text-emerald-400">+{formatCurrency(day.inflow)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Outflows</div>
                          <div className="text-sm font-bold text-red-400">-{formatCurrency(day.outflow)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">Net</div>
                          <div className={cn("text-sm font-bold", day.net >= 0 ? "text-blue-400" : "text-amber-400")}>
                            {day.net >= 0 ? '+' : ''}{formatCurrency(day.net)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>

              {/* Sweep Settings */}
              <GlassCard>
                <div className="p-6 border-b border-white/5 bg-slate-950/30">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Settings className="w-5 h-5 text-violet-400" />
                    Sweep Settings
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-bold text-emerald-400">Auto-Sweep Active</span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Excess cash is automatically swept to money market fund (SGOV) daily at 4 PM ET
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-slate-400">Sweep Target</span>
                      <span className="text-sm font-bold text-white">$5,000 per account</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-slate-400">Sweep Vehicle</span>
                      <span className="text-sm font-bold text-white">SGOV (Treasury MM)</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-sm text-slate-400">Current Yield</span>
                      <span className="text-sm font-bold text-emerald-400">5.25% APY</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-slate-400">Last Sweep</span>
                      <span className="text-sm font-bold text-white">Today 4:00 PM</span>
                    </div>
                  </div>

                  <button className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl transition-all border border-white/10 flex items-center justify-center gap-2">
                    <Settings className="w-4 h-4" />
                    Configure Sweep Rules
                  </button>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'accounts' && (
            <motion.div
              key="accounts"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <GlassCard>
                <div className="p-6 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-400" />
                    Account Cash Balances
                  </h3>
                  <button className="text-xs font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest">
                    Export CSV
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                        <th className="py-4 px-6">Account</th>
                        <th className="py-4 px-6">Household</th>
                        <th className="py-4 px-6 text-right">Available Cash</th>
                        <th className="py-4 px-6 text-right">Pending</th>
                        <th className="py-4 px-6 text-right">Sweep Target</th>
                        <th className="py-4 px-6 text-center">Sweep</th>
                        <th className="py-4 px-6 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {(cashSummary?.accounts || []).map((account, i) => {
                        const aboveTarget = account.availableCash > account.sweepTarget;
                        return (
                          <tr key={account.accountId} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                            <td className="py-4 px-6 font-bold text-white">{account.accountName}</td>
                            <td className="py-4 px-6 text-slate-400">{account.householdName}</td>
                            <td className="py-4 px-6 text-right font-mono font-bold text-emerald-400">
                              {formatFullCurrency(account.availableCash)}
                            </td>
                            <td className="py-4 px-6 text-right font-mono text-slate-500">
                              {formatFullCurrency(account.pendingSettlement)}
                            </td>
                            <td className="py-4 px-6 text-right font-mono text-slate-500">
                              {formatFullCurrency(account.sweepTarget)}
                            </td>
                            <td className="py-4 px-6 text-center">
                              {account.sweepEnabled ? (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                  ON
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-800 text-slate-500 border border-white/5">
                                  OFF
                                </span>
                              )}
                            </td>
                            <td className="py-4 px-6 text-right">
                              {aboveTarget ? (
                                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                  Sweep Pending
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-slate-800 text-slate-400 border border-white/5">
                                  At Target
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {(!cashSummary?.accounts || cashSummary.accounts.length === 0) && (
                        <tr>
                          <td colSpan={7} className="py-12 text-center text-slate-500 italic">
                            No accounts found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'transactions' && (
            <motion.div
              key="transactions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <GlassCard>
                <div className="p-6 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-violet-400" />
                    Recent Cash Activity (30 Days)
                  </h3>
                  <div className="flex gap-2">
                    {['All', 'Dividends', 'Settlements', 'Fees'].map(filter => (
                      <button
                        key={filter}
                        className="px-3 py-1 rounded-lg text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="divide-y divide-white/5">
                  {recentTransactions.map((tx) => (
                    <div key={tx.id} className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors">
                      <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center border border-white/5">
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-white">{tx.description}</div>
                        <div className="text-xs text-slate-500">
                          {tx.accountName} • {new Date(tx.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={cn(
                          "font-bold font-mono",
                          tx.amount >= 0 ? "text-emerald-400" : "text-red-400"
                        )}>
                          {tx.amount >= 0 ? '+' : ''}{formatFullCurrency(tx.amount)}
                        </div>
                        <div className="text-[10px] text-slate-500 uppercase">{tx.type}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </div>
                  ))}
                  {recentTransactions.length === 0 && (
                    <div className="py-12 text-center text-slate-500 italic">
                      No recent transactions
                    </div>
                  )}
                </div>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Container>
  );
};

export default RetailCashDashboard;
